import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";
import { CountryCode } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";

import { CreditScorePill } from "../credit-score-pill";
import { FilesSection } from "./FilesSection";
import { FinancialYearCharts } from "./FinancialYearCharts";
import { KeyRatiosTable } from "./KeyRatiosTable";
import { SectionNav } from "./SectionNav";
import { SourcePin } from "./SourcePin";
import type {
  RepaymentCapacityAccountingData,
  RepaymentCapacityTabProps,
} from "./types";

function SectionCard({
  id,
  title,
  pins,
  headerRight,
  children,
  scrollOffset = 140,
  collapsible = false,
  defaultOpen = true,
}: {
  id: string;
  title: string;
  pins?: React.ReactNode;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  scrollOffset?: number;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      id={id}
      className="overflow-hidden rounded-xl border border-border bg-white shadow-sm"
      style={{ scrollMarginTop: scrollOffset }}
    >
      {collapsible ? (
        <div
          className={cn(
            "flex w-full items-center justify-between px-6 py-4",
            open ? "border-b border-border" : "",
          )}
        >
          <button
            type="button"
            className="flex items-center gap-2 text-left cursor-pointer"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            <h2 className="text-section">{title}</h2>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-primary-400 transition-transform duration-200",
                open && "rotate-180",
              )}
            />
          </button>
          <div className="flex items-center gap-2">
            {pins && <div className="flex items-center gap-1.5">{pins}</div>}
            {headerRight}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <h2 className="text-section">{title}</h2>
            {pins && <div className="flex items-center gap-1.5">{pins}</div>}
          </div>
          {headerRight}
        </div>
      )}
      {open && <div className="p-6">{children}</div>}
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-label mb-0.5">{label}</div>
      <div className="text-base-strong">{value}</div>
    </div>
  );
}

function formatInsightPeriod(
  fromDate: string,
  toDate: string,
  countryCode: CountryCode,
): string {
  const locale = countryCode === CountryCode.FI ? "fi-FI" : "sv-SE";
  const fmt = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString(locale, { year: "numeric", month: "short" });
  };
  return `${fmt(fromDate)} – ${fmt(toDate)}`;
}

const EMPTY_ACCOUNTING_DATA: RepaymentCapacityAccountingData = {
  profitLossEntries: [],
  financialRatioEntries: [],
  balanceSheetEntries: [],
};

export function RepaymentCapacityTab({
  selfEstimatedRevenueValue,
  selfEstimatedHistoricRevenueValue,
  accountingDataFiles,
  bankingDataFiles,
  annualReportFiles,
  insightsRevenueLast12Months,
  insightsRevenueSinceFinancialReport,
  accountingData = EMPTY_ACCOUNTING_DATA,
  currency,
  countryCode,
  creditScoreColor,
  creditScoreStatus,
  applyingCompanyId,
  hasBankingData,
  revenueHeaderRight,
  hideNav = false,
  scrollOffset = 140,
  scrollContainerSelector,
  offsetTopPx = 0,
}: RepaymentCapacityTabProps) {
  const [formatCurrency] = useFormatCurrency("sv-SE", { currency });

  return (
    <div
      className={cn(
        "grid gap-8",
        hideNav ? "grid-cols-1" : "grid-cols-[220px_1fr]",
      )}
    >
      {/* Left column — nav */}
      {!hideNav && (
        <div>
          <SectionNav
            scrollContainerSelector={scrollContainerSelector}
            offsetTopPx={offsetTopPx}
          />
        </div>
      )}

      {/* Right column — sections */}
      <div className="flex flex-col gap-6">
        {/* Section 1: Revenue Overview (Credit Score + Customer Estimated + Calculated) */}
        <SectionCard
          id="revenue-overview"
          title="Revenue overview"
          headerRight={revenueHeaderRight}
          scrollOffset={scrollOffset}
        >
          <div className="-m-6 flex flex-col divide-y divide-border">
            {/* Row 1: Credit score */}
            <div className="flex items-center gap-3 px-6 py-4">
              <span className="text-sm font-medium text-foreground">
                Credit score
              </span>
              <CreditScorePill
                color={creditScoreColor}
                status={creditScoreStatus}
              />
            </div>

            {/* Row 2: Customer estimated + Calculated estimate */}
            <div className="grid sm:grid-cols-2 divide-y divide-border sm:divide-x sm:divide-y-0">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    Customer estimated
                  </span>
                  <SourcePin source="application" />
                </div>
                <div className="flex flex-col gap-4">
                  <MetricRow
                    label="Last 12 months"
                    value={formatCurrency(selfEstimatedHistoricRevenueValue)}
                  />
                  <MetricRow
                    label="Next 12 months"
                    value={formatCurrency(selfEstimatedRevenueValue)}
                  />
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    Calculated estimate
                  </span>
                  <SourcePin source="kreaEstimated" />
                </div>
                <div className="flex flex-col gap-4">
                  <MetricRow
                    label="Last 12 months"
                    value={
                      insightsRevenueLast12Months != null
                        ? `${formatCurrency(insightsRevenueLast12Months.value)} · ${formatInsightPeriod(insightsRevenueLast12Months.fromDate, insightsRevenueLast12Months.toDate, countryCode)}`
                        : "Not calculated"
                    }
                  />
                  <MetricRow
                    label="Since latest financial report"
                    value={
                      insightsRevenueSinceFinancialReport != null
                        ? `${formatCurrency(insightsRevenueSinceFinancialReport.value)} · ${formatInsightPeriod(insightsRevenueSinceFinancialReport.fromDate, insightsRevenueSinceFinancialReport.toDate, countryCode)}`
                        : "Not calculated"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          {accountingDataFiles?.length ||
          bankingDataFiles?.length ||
          annualReportFiles?.length ? (
            <div className="mt-6 border-t border-border pt-6">
              <p className="mb-3 text-sm font-medium text-foreground">
                Source documents
              </p>
              <FilesSection
                accountingDataFiles={accountingDataFiles ?? null}
                annualReportFiles={annualReportFiles ?? null}
                bankingDataFiles={bankingDataFiles ?? null}
              />
            </div>
          ) : null}
        </SectionCard>

        {/* Section 2: Financial Year Data */}
        <SectionCard
          id="financial-year"
          title="Financial year data"
          pins={<SourcePin source="annualReport" />}
          scrollOffset={scrollOffset}
        >
          <FinancialYearCharts
            profitLossEntries={accountingData.profitLossEntries}
            ratioEntries={accountingData.financialRatioEntries}
            currency={currency}
            applyingCompanyId={applyingCompanyId}
            hasBankingData={hasBankingData}
          />
        </SectionCard>

        {/* Section 3: Key Financial Ratios */}
        <SectionCard
          id="key-ratios"
          title="Financial statements & key financial ratios"
          pins={<SourcePin source="annualReport" />}
          scrollOffset={scrollOffset}
          collapsible
          defaultOpen={true}
        >
          <KeyRatiosTable
            profitLossEntries={accountingData.profitLossEntries}
            ratioEntries={accountingData.financialRatioEntries}
            balanceEntries={accountingData.balanceSheetEntries}
            currency={currency}
          />
        </SectionCard>
      </div>
    </div>
  );
}
